/// <reference path="koyori.ts" />
/// <reference path="animation.ts" />
/// <reference path="input_text.ts" />
/// <reference path="button.ts" />
/// <reference path="form.ts" />

/**
 * ダイアログコンポーネントです。中に必ずkoy-dialog-contentタグを指定する必要があります。
 * <dl>
 * <dt>属性</dt>
 * <dd>type: ダイアログの種別（modal: モーダル（デフォルト）、modeless: モードレス、transient: 背景クリックで閉じられるモーダル）</dd>
 * <dd>open-animation: 開く際のアニメーション名（省略時: fade-in）
 * <dd>close-animation: 閉じる際のアニメーション名（省略時: fade-out）
 * <dd>position: ダイアログコンポーネントの表示位置（top、top-fit、bottom、bottom-fit、left、left-fit、right、right-fit、top-left、top-right、bottom-left、bottom-right、center、center-small、center-large、center-fitのいずれか）</dd>
 * <dd>open: ダイアログが表示されている場合に指定される</dd>
 * <dt>イベント</dt>
 * <dd>open-pre: ダイアログが開く前に発生する（Event.preventDefaultで中止する）</dd>
 * <dd>open: ダイアログが開いた後に発生する（Event.preventDefaultで閉じる）</dd>
 * <dd>close-pre: ダイアログが閉じる前に発生する（Event.preventDefaultで中止する）</dd>
 * <dd>close: ダイアログが閉じた後に発生する（Event.preventDefaultで開く）</dd>
 * </dl>
 */
class KoyoriDialog extends KoyoriElement {

	/**
	 * アラートダイアログを表示します。
	 *
	 * @param message メッセージ
	 * @param func コールバック関数
	 */
	static alert(message: string, func?: KoyoriDialogResult) {
		let dialog = this.createStaticDialog();
		dialog.innerHTML = '<koy-dialog-content class="koy-pre"><p>' + message + '</p><koy-form></koy-form><footer><koy-button data-value="true">OK</koy-button></footer></koy-dialog-content>';
		dialog.open();
		dialog.querySelector('footer>koy-button').addEventListener('click', () => {
			let result = true;
			if (func != undefined) {
				dialog.changeButtonDisabled(true);
				result = func(true, undefined, dialog);
				dialog.changeButtonDisabled(false);
			}
			if (result != false) {
				dialog.close();
			}
		}, true);
	}

	/**
	 * 確認ダイアログを表示します。
	 *
	 * @param message メッセージ
	 * @param func コールバック関数
	 */
	static confirm(message: string, func?: KoyoriDialogResult) {
		let dialog = this.createStaticDialog();
		dialog.innerHTML = '<koy-dialog-content class="koy-pre"><p>' + message + '</p><koy-form></koy-form><footer><koy-button data-value="false">CANCEL</koy-button> <koy-button data-value="true">OK</koy-button></footer></koy-dialog-content>';
		dialog.open();
		KoyoriElement.querySelectorAll(dialog, 'footer>koy-button').forEach(button => {
			button.addEventListener('click', event => {
				let button = (<HTMLElement>(<MouseEvent>event).target).closest('koy-button');
				dialog.changeButtonDisabled(true);
				let result = func(button.getAttribute('data-value') == 'true', undefined, dialog);
				dialog.changeButtonDisabled(false);
				if (result != false) {
					dialog.close();
				}
			}, true);
		});
	}

	/**
	 * プロンプトダイアログを表示します。
	 *
	 * @param message メッセージ
	 * @param value 初期値
	 * @param func コールバック関数
	 */
	static prompt(message: string, value?: string, func?: KoyoriDialogResult) {
		let dialog = this.createStaticDialog();
		dialog.innerHTML = '<koy-dialog-content class="koy-pre"><p>' + message + '</p><p><koy-input-text class="prompt-value" value="' + (value == undefined ? '' : value) + '"></koy-input-text></p><koy-form></koy-form><footer><koy-button data-value="false">CANCEL</koy-button> <koy-button data-value="true">OK</koy-button></footer></koy-dialog-content>';
		dialog.open();
		KoyoriElement.querySelectorAll(dialog, 'footer>koy-button').forEach(button => {
			button.addEventListener('click', () => {
				let result;
				let button = (<HTMLElement>(<MouseEvent>event).target).closest('koy-button');
				if (button.getAttribute('data-value') == 'false') {
					result = true;
				} else {
					dialog.changeButtonDisabled(true);
					result = func(true, (<KoyoriInputText>dialog.querySelector('koy-input-text.prompt-value')).value, dialog);
					dialog.changeButtonDisabled(false);
				}
				if (result != false) {
					dialog.close();
				}
			}, true);
		});
	}

	/**
	 * staticメソッドで使用するダイアログの雛形を作成します。
	 */
	private static createStaticDialog(): KoyoriDialog {
		let dialog = <KoyoriDialog>document.createElement('koy-dialog');
		dialog.setAttribute('open-animation', 'fade-in-down');
		dialog.setAttribute('close-animation', 'fade-out-up');
		dialog.setAttribute('position', 'top');
		document.body.appendChild(dialog);
		dialog.addEventListener('open', () => {
			(<HTMLElement>dialog.querySelector('.koy-input, .koy-button')).focus();
		});
		dialog.addEventListener('close', () => {
			setTimeout(() => {
				let parent = dialog.parentNode;
				if (parent != null) {
					parent.removeChild(dialog);
				}
			}, 300);
		});
		return dialog;
	}

	constructor() {
		super();

		KoyoriDialog.querySelectorAll(this, "koy-dialog-content").forEach(content => content.style.display = 'none');

		this.addEventListener("click", event => {
			if (event.target instanceof HTMLElement) {
				let button = (<HTMLElement>event.target).closest('koy-button');
				if (button != null && button.getAttribute('type') == 'cancel') {
					event.preventDefault();
					this.close();
					return;
				}
			}
			if (event.target != this) {
				return;
			}
			if (this.getAttribute('type') == 'transient' && this.getAttribute('open') != null) {
				this.close();
			}
		});

		this.addEventListener('keyup', event => {
			if (event.keyCode == 27) {
				// ESCキー押下時にダイアログを閉じる
				this.close();
			}
		});
	}

	/**
	 * ダイアログを開きます。
	 * 
	 * @param formReset trueの場合は内部のフォームをリセットします
	 */
	open(formReset = false): void {
		if (this.getAttribute('open') != null && this.style.display != 'none') {
			return;
		}
		if (KoyoriElement.fireEvent(this, 'open-pre') == false) {
			return;
		}
		if (formReset) {
			let form = this.getForm();
			if (form != null) {
				form.reset();
			}
		}
		KoyoriAnimation.fadeIn(this);
		setTimeout(() => {
			let animation = this.getAttribute('open-animation') || 'fade-in';
			KoyoriDialog.querySelectorAll(this, "koy-dialog-content").forEach(content => {
				KoyoriAnimation.show(content, animation);
				setTimeout(() => {
					content.scrollTop = 0;
				}, 10);
			});
			setTimeout(() => {
				this.setAttribute('open', '');
				if (KoyoriElement.fireEvent(this, 'open') == false) {
					this.close();
				}
			}, 0);
		}, 0);
	}

	/**
	 * ダイアログを閉じます。
	 */
	close(): void {
		if (this.getAttribute('open') == null && this.style.display == 'none') {
			return;
		}
		if (KoyoriElement.fireEvent(this, 'close-pre') == false) {
			return;
		}
		let animation = this.getAttribute('close-animation') || 'fade-out';
		KoyoriDialog.querySelectorAll(this, "koy-dialog-content").forEach(content => KoyoriAnimation.hide(content, animation));
		setTimeout(() => {
			KoyoriAnimation.fadeOut(this);
			setTimeout(() => {
				this.removeAttribute('open');
				if (KoyoriElement.fireEvent(this, 'close') == false) {
					this.open();
				}
			}, 300);
		}, 0);
	}

	/**
	 * ダイアログにコンテンツを追加します。
	 * 
	 * @param content コンテンツ
	 * @param autoShow ダイアログが閉じられている場合に自動的に表示する場合はtrue
	 */
	appendContent(content: KoyoriDialogContent, autoShow = true) {
		if (content.tagName != "KOY-DIALOG-CONTENT") {
			throw new Error("content must be koy-dialog-content");
		}
		let animation = this.getAttribute('open-animation') || 'fade-in';
		KoyoriAnimation.show(content, animation);
		setTimeout(() => {
			this.appendChild(content);
			this.open();
		}, 0);
	}

	/**
	 * 内部のフォームを取得します。存在しない場合はnullを返します。
	 */
	getForm(): KoyoriForm {
		return <KoyoriForm>this.querySelector('koy-form');
	}

	/**
	 * 最初の有効な入力エレメントにフォーカスします。
	 */
	focusFirstInput(): void {
		let element = this.getForm() || this;
		let target = <HTMLElement>element.querySelector('[name]:not([disabled])');
		if (target != null) {
			target.focus();
		}
	}

	/**
	 * フッタにあるボタンの有効・無効を切り替えます。
	 * 
	 * @param disabled
	 */
	private changeButtonDisabled(disabled: boolean): void {
		KoyoriElement.querySelectorAll(this, 'footer>koy-button').forEach(button => {
			(<KoyoriButton>button).disabled = disabled;
		});
	}
}
window.customElements.define('koy-dialog', KoyoriDialog);

/**
 * ダイアログの中身のコンポーネントです。
 * <dl>
 * <dt>属性</dt>
 * </dl>
 */
class KoyoriDialogContent extends KoyoriElement {

	/**
	 * ダイアログからコンテンツを除去します。
	 * 
	 * @param autoClose 対象のコンテンツが最後の場合にダイアログを閉じる場合はtrue
	 */
	remove(autoClose = true) {
		let dialog = <KoyoriDialog>this.closest('koy-dialog');
		let animation = dialog.getAttribute('close-animation') || 'fade-out';
		KoyoriAnimation.hide(this, animation);
		setTimeout(() => {
			dialog.removeChild(this);
		}, 300);
		if (dialog.querySelectorAll('koy-dialog-content').length == 1) {
			dialog.close();
		}
	}
}
window.customElements.define('koy-dialog-content', KoyoriDialogContent);

/**
 * alert、confirm、prompt関数のコールバック関数用インタフェースです。
 * 戻り値にfalseを指定するとダイアログを閉じません。
 */
interface KoyoriDialogResult {

	/**
	 * @param result ダイアログの結果（OKボタン: true、CANCELボタン: false）
	 * @param input 入力値
	 * @param dialog ダイアログ
	 */
	(result: boolean, input: string, dialog: KoyoriDialog): boolean;

}
